import {
  Conversation,
  ScheduleQueryParams,
  TimeOffRequestListQueryParams,
  toDateString,
} from '@nextforce/api'
import React, { useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useAuthentication } from 'authentication/context'
import { useChatConfig } from 'chat/config/queries/get'
import { useConversations } from 'chat/conversations/queries/list'
import { useMessages } from 'chat/messages/queries/list'

import { useRecipients } from 'chat/members/queries/recipients'
import { useCompanyPreferences } from 'company/queries/list'
import { useMonthlySummary } from 'home/monthly_summary/queries/list'
import { useUpdateApp } from 'home/update_app'
import moment from 'moment'
import { useNotifications } from 'notifications/queries/list'
import { useNotificationsSummary } from 'notifications/queries/summary'
import { useTimeOffRequestList } from 'request_time_off/queries/list'
import { helperScheduleQueryParams, useSchedule } from 'schedule/queries/list'
import { StatusBoundary } from 'shared/components/status_boundary'
import { automaticRetryQuery, onManualRetry } from 'shared/queries/utils'
import { generateUniqueId } from 'shared/utils'
import { useStores } from 'stores/queries/list'
import { useUser } from 'users/queries/detail'
import {
  ConceptQueryErrorProvider,
  QueryErrors,
} from './concept_query_error_context'
import { t } from './i18l'

export type ConversationsSection = {
  type: string
  data: Conversation[]
  unread: number
}

export const openShiftsScheduleQueryParams: ScheduleQueryParams = {
  schedule: { calendar: 'helper', openShiftsOnly: true },
}

const timeOffRequestListQueryParams: TimeOffRequestListQueryParams = {
  role: 'ROLE_OBSERVER',
  startDate: toDateString(moment()),
}

interface Props {
  children: React.ReactNode
}

export function ConceptDataHandler(props: Props) {
  const { profileNeedsReview } = useAuthentication()
  const client = useQueryClient()

  // Critical Queries (Home Screen will not load without these)
  const userQuery = useUser(automaticRetryQuery)

  // Non-Critical Queries (Home Screen will load without these)
  const messagesQuery = useMessages(data => data, automaticRetryQuery)
  const conversationsQuery = useConversations(automaticRetryQuery)
  const chatConfigQuery = useChatConfig(automaticRetryQuery)
  const notificationsQuery = useNotifications(automaticRetryQuery)
  const storesQuery = useStores(data => data.stores, automaticRetryQuery)
  const companyPreferencesQuery = useCompanyPreferences(
    storesQuery.data && storesQuery.data.length > 0
      ? storesQuery?.data[0].company?.id
      : undefined,
    automaticRetryQuery
  )
  const recipientsQuery = useRecipients()
  const helperScheduleQuery = useSchedule(
    helperScheduleQueryParams,
    generateUniqueId,
    undefined,
    undefined,
    automaticRetryQuery
  )
  const openShiftsScheduleQuery = useSchedule(
    openShiftsScheduleQueryParams,
    generateUniqueId,
    userQuery.data?.permissions?.canSeeNextShifts,
    undefined,
    automaticRetryQuery
  )
  const monthlySummaryQuery = useMonthlySummary(
    userQuery.data?.permissions?.canSeeMonthlySummary,
    undefined,
    automaticRetryQuery
  )
  const notificationsSummaryQuery = useNotificationsSummary(
    undefined,
    automaticRetryQuery
  )
  const updateAppQuery = useUpdateApp()
  const timeOffRequestsQuery = useTimeOffRequestList(
    timeOffRequestListQueryParams,
    undefined,
    userQuery.data?.permissions?.canApproveTimeOffRequests,
    undefined,
    automaticRetryQuery
  )

  const isLoadingHomeScreen =
    helperScheduleQuery.isLoading ||
    openShiftsScheduleQuery.isLoading ||
    notificationsSummaryQuery.isLoading ||
    monthlySummaryQuery.isLoading ||
    updateAppQuery.isLoading ||
    timeOffRequestsQuery.isLoading

  const isLoadingUser = userQuery.isLoading

  const isLoadingStores =
    storesQuery.isLoading || companyPreferencesQuery.isLoading

  const isLoadingMessages =
    messagesQuery.isLoading ||
    conversationsQuery.isLoading ||
    chatConfigQuery.isLoading ||
    recipientsQuery.isLoading

  const isLoadingNotifications = notificationsQuery.isLoading

  const isLoadingOtherQueries =
    isLoadingHomeScreen ||
    isLoadingStores ||
    isLoadingMessages ||
    isLoadingNotifications

  const isLoadingCriticalQueries = isLoadingUser
  const isCriticalDataLoaded = !!userQuery.data

  const isCriticalError = userQuery.isError

  const isOtherError =
    conversationsQuery.isError ||
    notificationsQuery.isError ||
    timeOffRequestsQuery.isError ||
    monthlySummaryQuery.isError ||
    updateAppQuery.isError ||
    openShiftsScheduleQuery.isError ||
    helperScheduleQuery.isError ||
    companyPreferencesQuery.isError ||
    chatConfigQuery.isError ||
    messagesQuery.isError ||
    storesQuery.isError ||
    notificationsSummaryQuery.isError ||
    recipientsQuery.isError

  const criticalQueryErrors: QueryErrors = useMemo(() => {
    const filteredErrors = Object.entries({
      USER_ERROR: userQuery.error,
    }).filter(([_, value]) => value !== null)
    return Object.fromEntries(filteredErrors)
  }, [userQuery.error])

  const allQueryErrors: QueryErrors = useMemo(() => {
    const filteredErrors = Object.entries({
      ...criticalQueryErrors,
      STORES_ERROR: storesQuery.error,
      COMPANY_PREFERENCES_ERROR: companyPreferencesQuery.error,
      CHAT_CONFIG_ERROR: chatConfigQuery.error,
      CONVERSATIONS_ERROR: conversationsQuery.error,
      HELPER_SCHEDULE_ERROR: helperScheduleQuery.error,
      MESSAGES_ERROR: messagesQuery.error,
      MONTHLY_SUMMARY_ERROR: monthlySummaryQuery.error,
      NOTIFICATIONS_SUMMARY_ERROR: notificationsSummaryQuery.error,
      OPEN_SHIFTS_SCHEDULE_ERROR: openShiftsScheduleQuery.error,
      TIME_OFF_REQUESTS_ERROR: timeOffRequestsQuery.error,
      UPDATE_APP_ERROR: updateAppQuery.error,
      NOTIFICATIONS_ERROR: notificationsQuery.error,
      RECIPIENTS_ERROR: recipientsQuery.error,
    }).filter(([_, value]) => value !== null)

    return Object.fromEntries(filteredErrors)
  }, [
    chatConfigQuery.error,
    companyPreferencesQuery.error,
    conversationsQuery.error,
    criticalQueryErrors,
    helperScheduleQuery.error,
    messagesQuery.error,
    monthlySummaryQuery.error,
    notificationsQuery.error,
    notificationsSummaryQuery.error,
    openShiftsScheduleQuery.error,
    recipientsQuery.error,
    storesQuery.error,
    timeOffRequestsQuery.error,
    updateAppQuery.error,
  ])

  return (
    <StatusBoundary
      isLoading={isLoadingCriticalQueries || !isCriticalDataLoaded}
      loadingMessage={
        profileNeedsReview
          ? t('loadingAccount')
          : isLoadingStores
          ? t('loadingStores')
          : isLoadingNotifications
          ? t('loadingNotifications')
          : isLoadingMessages
          ? t('loadingMessages')
          : isLoadingHomeScreen
          ? t('loadingHomeScreen')
          : t('loadingData')
      }
      error={undefined}
      errors={criticalQueryErrors}
      onRetry={() => onManualRetry(client)}
    >
      <ConceptQueryErrorProvider queryErrors={allQueryErrors}>
        {props.children}
      </ConceptQueryErrorProvider>
    </StatusBoundary>
  )
}
